import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Define the database schema
interface ExAnDB extends DBSchema {
  documents: {
    key: string;
    value: {
      id: string;
      name: string;
      type: 'pdf' | 'image' | 'audio';
      size: number;
      data: ArrayBuffer;
      uploadedAt: Date;
      lastModified: Date;
      tags: string[];
      projectId?: string;
    };
  };
  
  extractions: {
    key: string;
    value: {
      id: string;
      documentId: string;
      extractionType: 'gemma' | 'tesseract';
      rawText: string;
      structuredData: any;
      confidence: number;
      processingTime: number;
      createdAt: Date;
      updatedAt: Date;
      corrections?: Array<{
        original: string;
        corrected: string;
        timestamp: Date;
      }>;
    };
    indexes: {
      'by-document': string;
      'by-date': Date;
    };
  };
  
  projects: {
    key: string;
    value: {
      id: string;
      name: string;
      description: string;
      createdAt: Date;
      updatedAt: Date;
      settings: {
        defaultEngine: 'gemma' | 'tesseract' | 'auto';
        autoProcess: boolean;
        language: string;
      };
    };
  };
  
  analytics: {
    key: string;
    value: {
      id: string;
      projectId: string;
      type: 'extraction' | 'usage' | 'performance';
      data: any;
      timestamp: Date;
    };
    indexes: {
      'by-project': string;
      'by-type': string;
    };
  };
  
  settings: {
    key: string;
    value: any;
  };
  
  licenses: {
    key: string;
    value: {
      id: string;
      token: string;
      tier: 'free' | 'pro' | 'enterprise';
      expiresAt: Date;
      features: string[];
      quotas: {
        documentsPerMonth: number;
        storageGB: number;
      };
      usage: {
        documentsProcessed: number;
        storageUsed: number;
        lastReset: Date;
      };
    };
  };
  
  syncQueue: {
    key: number;
    value: {
      id: number;
      type: 'create' | 'update' | 'delete';
      collection: string;
      data: any;
      timestamp: Date;
      attempts: number;
      lastAttempt?: Date;
      error?: string;
    };
    autoIncrement: true;
  };
}

class DatabaseService {
  private db: IDBPDatabase<ExAnDB> | null = null;
  private readonly DB_NAME = 'exan-db';
  private readonly DB_VERSION = 1;
  
  async initialize(): Promise<void> {
    if (this.db) return;
    
    this.db = await openDB<ExAnDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Documents store
        if (!db.objectStoreNames.contains('documents')) {
          db.createObjectStore('documents', { keyPath: 'id' });
        }
        
        // Extractions store with indexes
        if (!db.objectStoreNames.contains('extractions')) {
          const extractionsStore = db.createObjectStore('extractions', { keyPath: 'id' });
          extractionsStore.createIndex('by-document', 'documentId');
          extractionsStore.createIndex('by-date', 'createdAt');
        }
        
        // Projects store
        if (!db.objectStoreNames.contains('projects')) {
          db.createObjectStore('projects', { keyPath: 'id' });
        }
        
        // Analytics store with indexes
        if (!db.objectStoreNames.contains('analytics')) {
          const analyticsStore = db.createObjectStore('analytics', { keyPath: 'id' });
          analyticsStore.createIndex('by-project', 'projectId');
          analyticsStore.createIndex('by-type', 'type');
        }
        
        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
        
        // Licenses store
        if (!db.objectStoreNames.contains('licenses')) {
          db.createObjectStore('licenses', { keyPath: 'id' });
        }
        
        // Sync queue with auto-increment
        if (!db.objectStoreNames.contains('syncQueue')) {
          db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        }
      },
      
      blocked() {
        console.warn('Database upgrade blocked by open connections');
      },
      
      blocking() {
        console.warn('Current database blocking upgrade');
        // Close connection to allow upgrade
        db?.close();
      },
      
      terminated() {
        console.error('Database connection terminated unexpectedly');
      },
    });
  }
  
  private async ensureInitialized(): Promise<IDBPDatabase<ExAnDB>> {
    if (!this.db) {
      await this.initialize();
    }
    return this.db!;
  }
  
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
  
  // Document operations
  async saveDocument(document: ExAnDB['documents']['value']): Promise<void> {
    const db = await this.ensureInitialized();
    await db.put('documents', document);
    
    // Add to sync queue if online (non-blocking)
    if (navigator.onLine) {
      try {
        await this.addToSyncQueue('create', 'documents', document);
      } catch (error) {
        console.warn('Failed to add to sync queue:', error);
        // Don't fail the save operation
      }
    }
  }
  
  async updateDocument(id: string, updates: Partial<ExAnDB['documents']['value']>): Promise<void> {
    const db = await this.ensureInitialized();
    const existing = await db.get('documents', id);
    if (!existing) throw new Error(`Document ${id} not found`);
    
    const updated = { ...existing, ...updates };
    await db.put('documents', updated);
    
    // Add to sync queue if online
    if (navigator.onLine) {
      await this.addToSyncQueue('update', 'documents', updated);
    }
  }
  
  async getDocument(id: string): Promise<ExAnDB['documents']['value'] | undefined> {
    const db = await this.ensureInitialized();
    return db.get('documents', id);
  }
  
  async getAllDocuments(projectId?: string): Promise<ExAnDB['documents']['value'][]> {
    const db = await this.ensureInitialized();
    const allDocs = await db.getAll('documents');
    
    if (projectId) {
      return allDocs.filter(doc => doc.projectId === projectId);
    }
    return allDocs;
  }
  
  async deleteDocument(id: string): Promise<void> {
    const db = await this.ensureInitialized();
    await db.delete('documents', id);
    
    // Also delete related extractions
    const tx = db.transaction(['extractions'], 'readwrite');
    const index = tx.objectStore('extractions').index('by-document');
    const keys = await index.getAllKeys(id);
    
    for (const key of keys) {
      await tx.objectStore('extractions').delete(key);
    }
    
    await tx.done;
    
    // Add to sync queue
    await this.addToSyncQueue('delete', 'documents', { id });
  }
  
  // Extraction operations
  async saveExtraction(extraction: ExAnDB['extractions']['value']): Promise<void> {
    const db = await this.ensureInitialized();
    await db.put('extractions', extraction);
    
    // Track analytics
    await this.trackAnalytics({
      type: 'extraction',
      data: {
        documentId: extraction.documentId,
        engine: extraction.extractionType,
        processingTime: extraction.processingTime,
        confidence: extraction.confidence,
      },
    });
    
    // Add to sync queue
    await this.addToSyncQueue('create', 'extractions', extraction);
  }
  
  async getExtractionsByDocument(documentId: string): Promise<ExAnDB['extractions']['value'][]> {
    const db = await this.ensureInitialized();
    const index = db.transaction('extractions').objectStore('extractions').index('by-document');
    return index.getAll(documentId);
  }
  
  async getExtraction(id: string): Promise<ExAnDB['extractions']['value'] | undefined> {
    const db = await this.ensureInitialized();
    return db.get('extractions', id);
  }
  
  async addCorrection(extractionId: string, correction: {
    field: string;
    oldValue: any;
    newValue: any;
  }): Promise<void> {
    const db = await this.ensureInitialized();
    const extraction = await db.get('extractions', extractionId);
    if (!extraction) throw new Error(`Extraction ${extractionId} not found`);
    
    const corrections = extraction.corrections || [];
    corrections.push({
      original: correction.oldValue,
      corrected: correction.newValue,
      timestamp: new Date(),
    });
    
    await db.put('extractions', {
      ...extraction,
      corrections,
      updatedAt: new Date(),
    });
    
    // Add to sync queue
    await this.addToSyncQueue('update', 'extractions', {
      ...extraction,
      corrections,
      updatedAt: new Date(),
    });
  }
  
  async updateExtractionCorrections(
    extractionId: string,
    corrections: Array<{ original: string; corrected: string; timestamp: Date }>
  ): Promise<void> {
    const db = await this.ensureInitialized();
    const extraction = await db.get('extractions', extractionId);
    
    if (extraction) {
      extraction.corrections = [...(extraction.corrections || []), ...corrections];
      extraction.updatedAt = new Date();
      await db.put('extractions', extraction);
      
      // Add to sync queue
      await this.addToSyncQueue('update', 'extractions', extraction);
    }
  }
  
  // License and quota management
  async getCurrentLicense(): Promise<ExAnDB['licenses']['value'] | undefined> {
    const db = await this.ensureInitialized();
    const licenses = await db.getAll('licenses');
    
    // Return the most recent valid license
    return licenses
      .filter(l => new Date(l.expiresAt) > new Date())
      .sort((a, b) => new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime())[0];
  }
  
  async updateLicenseUsage(documentsProcessed: number, storageUsed: number): Promise<void> {
    const db = await this.ensureInitialized();
    const license = await this.getCurrentLicense();
    
    if (license) {
      license.usage.documentsProcessed += documentsProcessed;
      license.usage.storageUsed += storageUsed;
      await db.put('licenses', license);
    }
  }
  
  async checkQuota(type: 'documents' | 'storage'): Promise<{ allowed: boolean; remaining: number }> {
    const license = await this.getCurrentLicense();
    
    if (!license) {
      // Free tier defaults
      const freeQuotas = { documentsPerMonth: 50, storageGB: 1 };
      return { allowed: true, remaining: type === 'documents' ? freeQuotas.documentsPerMonth : freeQuotas.storageGB };
    }
    
    if (type === 'documents') {
      const remaining = license.quotas.documentsPerMonth - license.usage.documentsProcessed;
      return { allowed: remaining > 0, remaining };
    } else {
      const remaining = license.quotas.storageGB - license.usage.storageUsed;
      return { allowed: remaining > 0, remaining };
    }
  }
  
  // Analytics tracking
  async trackAnalytics(data: Partial<ExAnDB['analytics']['value']>): Promise<void> {
    const db = await this.ensureInitialized();
    const projectId = await this.getCurrentProjectId();
    
    const analytics: ExAnDB['analytics']['value'] = {
      id: crypto.randomUUID(),
      projectId: projectId || 'default',
      type: data.type || 'usage',
      data: data.data,
      timestamp: new Date(),
    };
    
    await db.put('analytics', analytics);
  }
  
  // Settings management
  async setSetting(key: string, value: any): Promise<void> {
    const db = await this.ensureInitialized();
    await db.put('settings', value, key);
  }
  
  async getSetting<T = any>(key: string): Promise<T | undefined> {
    const db = await this.ensureInitialized();
    return db.get('settings', key);
  }
  
  private async getCurrentProjectId(): Promise<string | null> {
    const projectId = await this.getSetting<string>('currentProjectId');
    return projectId ?? null;
  }
  
  // Sync queue management
  private async addToSyncQueue(
    type: 'create' | 'update' | 'delete',
    collection: string,
    data: any
  ): Promise<void> {
    const db = await this.ensureInitialized();
    
    // Generate a unique ID for sync queue
    const id = Date.now() + Math.random();
    
    await db.put('syncQueue', {
      id,
      type,
      collection,
      data,
      timestamp: new Date(),
      attempts: 0,
    });
  }
  
  async getPendingSyncItems(): Promise<ExAnDB['syncQueue']['value'][]> {
    const db = await this.ensureInitialized();
    return db.getAll('syncQueue');
  }
  
  async markSyncItemComplete(id: number): Promise<void> {
    const db = await this.ensureInitialized();
    await db.delete('syncQueue', id);
  }
  
  async markSyncItemFailed(id: number, error: string): Promise<void> {
    const db = await this.ensureInitialized();
    const item = await db.get('syncQueue', id);
    
    if (item) {
      item.attempts += 1;
      item.lastAttempt = new Date();
      item.error = error;
      
      // Remove if too many attempts
      if (item.attempts >= 3) {
        await db.delete('syncQueue', id);
      } else {
        await db.put('syncQueue', item);
      }
    }
  }
  
  // Storage management
  async getStorageEstimate(): Promise<{ usage: number; quota: number; percentage: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;
      
      return { usage, quota, percentage };
    }
    
    // Fallback for browsers without storage estimation
    return { usage: 0, quota: 0, percentage: 0 };
  }
  
  async requestPersistentStorage(): Promise<boolean> {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      return navigator.storage.persist();
    }
    return false;
  }
  
  // Cleanup and maintenance
  async cleanup(): Promise<void> {
    const db = await this.ensureInitialized();
    
    // Clean up old analytics (keep last 30 days)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 30);
    
    const tx = db.transaction(['analytics'], 'readwrite');
    const analyticsStore = tx.objectStore('analytics');
    const allAnalytics = await analyticsStore.getAll();
    
    for (const item of allAnalytics) {
      if (item.timestamp < cutoffDate) {
        await analyticsStore.delete(item.id);
      }
    }
    
    await tx.done;
  }
  
  // Export for debugging/backup
  async exportData(): Promise<any> {
    const db = await this.ensureInitialized();
    
    return {
      documents: await db.getAll('documents'),
      extractions: await db.getAll('extractions'),
      projects: await db.getAll('projects'),
      analytics: await db.getAll('analytics'),
      settings: await db.getAll('settings'),
      exportedAt: new Date().toISOString(),
    };
  }
}

// Export singleton instance
export const db = new DatabaseService();

// Export types for use in other files
export type Document = ExAnDB['documents']['value'];
export type Extraction = ExAnDB['extractions']['value'];
export type Project = ExAnDB['projects']['value'];
export type Analytics = ExAnDB['analytics']['value'];
export type License = ExAnDB['licenses']['value'];